/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Account Info MultiBalances Component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.11.0
 */

import { createElement, Fragment, useCallback, useEffect } from 'react';

import { FormGroup } from '@material-ui/core';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { icons } from '@assets/index';
import { Collapse, CollapsePanel, EllipseText, LinkBlock } from '@components/common';
import { CollapseStandardStyled } from '@components/common/Collapse/styled/CollapseStyled';
import {
    makeSelectPageAccountInfoTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import { modalOpen } from '@modules/modal/modalActions';
import { IRouteParams } from '@routes/constants/routesConstants';
import { formatBalance, formatDate } from '@utils/formatText';
import { stringFormatParams } from '@utils/stringFormat';

import { accountInfoApi } from '../store';
import AccountInfoMultiBalancesHistoryModal from './AccountInfoMultiBalancesHistoryModal';
import {
    AccountInfoMultiBalancesCollapseListItemStyled,
    AccountInfoMultiBalancesListItemBodyStyled,
    AccountInfoMultiBalancesListItemStyled,
    AccountInfoMultiBalancesListItemSubTitleStyled,
    AccountInfoMultiBalancesListItemTitleStyled,
    AccountInfoMultiBalancesListStyled,
} from './styled/AccountInfoMultiBalancesStyled';
import { PageAccountInfoTitleStyled } from './styled/PageAccountInfoStyled';

const AccountInfoFinancialMultiBalances = () => {

    const dispatch = useDispatch();

    const translationAccountInfo = useSelector(makeSelectPageAccountInfoTranslation());
    const translationCommon = useSelector(makeSelectTranslationCommon());

    const { clientId } = useParams<IRouteParams>();

    const {
        data: multiBalancesList,
        isError: multiBalancesListIsError,
        isLoading: multiBalancesListIsLoading,
    } = accountInfoApi.endpoints.getAccountInfoMultiBalanceList.useQuery({ clientId });

    const {
        data: exhaustedList,
        isError: exhaustedListIsError,
        isLoading: exhaustedListIsLoading,
    } = accountInfoApi.endpoints.getAccountInfoMultiBalanceExhaustedList.useQuery({ clientId });

    const translation = translationAccountInfo.financialInfo.multiBalances;

    const clickHandler = useCallback((expandedBalanceId) => {
        dispatch(modalOpen({
            render: () => createElement(AccountInfoMultiBalancesHistoryModal, {
                clientId,
                expandedBalanceId,
            }),
            modalParams: {},
        }));
    }, [clientId]);

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo({
                top: 9999,
            });
        }, 200);
    }, [exhaustedList, exhaustedListIsLoading]);

    return <Fragment>

        <PageAccountInfoTitleStyled>{translation.title}</PageAccountInfoTitleStyled>

        {
            !multiBalancesListIsError &&
            <Loader showLoader={multiBalancesListIsLoading}>
                <AccountInfoMultiBalancesListStyled>
                    {!multiBalancesList?.multiBalances.length && (
                            <span data-name="account-info-finance-no-multi-balances">{translation.emptyList}</span>
                    )}
                    {
                        multiBalancesList?.multiBalances.map(item => <Fragment key={item.expandedBalanceId}>
                            <AccountInfoMultiBalancesListItemStyled data-name="account-info-finance-multi-balances">
                                <FormGroup>
                                    <AccountInfoMultiBalancesListItemTitleStyled>
                                        <h3><EllipseText title={item.name}>{item.name}</EllipseText></h3>
                                        <AccountInfoMultiBalancesListItemSubTitleStyled>
                                            {
                                                stringFormatParams(translation.card.titleDates, {
                                                    from: formatDate(item.startDateTime, { skipZone: true }),
                                                    to: formatDate(item.endDateTime),
                                                })
                                            }
                                        </AccountInfoMultiBalancesListItemSubTitleStyled>
                                    </AccountInfoMultiBalancesListItemTitleStyled>
                                </FormGroup>
                                <AccountInfoMultiBalancesListItemBodyStyled>
                                    <FormGroup>
                                        <p>
                                            {translation.card.originalBalanceTitle}
                                            {' '}
                                            {formatBalance(item.originalBalance)}
                                            {' '}
                                            {translationCommon.currency}
                                        </p>
                                        <p>
                                            {translation.card.remainingTitle}
                                            {' '}
                                            {formatBalance(item.remaining)}
                                            {' '}
                                            {translationCommon.currency}
                                            <br/>
                                            <span className="date-time">
                                        {
                                            stringFormatParams(translation.card.remainingDate, {
                                                date: formatDate(moment().toString()),
                                            })
                                        }
                                    </span>
                                        </p>
                                    </FormGroup>
                                    <LinkBlock dataName="account-info-finance-multi-balances-more-btn" onClick={() => {
                                        clickHandler(item.expandedBalanceId);
                                    }}>
                                        {translation.card.linkMore}
                                    </LinkBlock>
                                </AccountInfoMultiBalancesListItemBodyStyled>
                            </AccountInfoMultiBalancesListItemStyled>
                        </Fragment>)
                    }
                </AccountInfoMultiBalancesListStyled>
            </Loader>
        }

        {
            !exhaustedListIsError && (exhaustedList?.multiBalances?.length ?? 0) > 0 &&
            <CollapseStandardStyled data-name='account-info-finance-exhausted-multi-balances'>
                <Collapse icon={icons.arrowDown}>
                    <CollapsePanel titleValue={translation.titleExhausted}>

                        {
                            exhaustedList?.multiBalances?.map(item => <Fragment key={item.expandedBalanceId}>
                                <AccountInfoMultiBalancesCollapseListItemStyled data-name='account-info-finance-exhausted-multi-balances-list'>
                                    <h5 data-name='account-info-finance-exhausted-multi-balances-title-btn' onClick={() => {
                                        clickHandler(item.expandedBalanceId);
                                    }}>{item.name}</h5>
                                    <p>{
                                        stringFormatParams(translation.card.titleDates, {
                                            from: formatDate(item.startDateTime, { skipZone: true }),
                                            to: formatDate(item.startDateTime),
                                        })
                                    }</p>
                                </AccountInfoMultiBalancesCollapseListItemStyled>
                            </Fragment>)
                        }

                    </CollapsePanel>
                </Collapse>
            </CollapseStandardStyled>
        }
    </Fragment>;
};

export default AccountInfoFinancialMultiBalances;
